/**
 * Pure spherical geometry helpers.
 *
 * Zero MapLibre, zero DOM — everything in here is unit-testable in node, which
 * is why the camera framing maths lives here rather than inside the component.
 *
 * All public helpers take and return `[longitude, latitude]` in that order.
 * `@geogame/data` stores `latlng` the other way round (REST Countries
 * convention); `lngLatOf()` is the single place that flip happens.
 */

import type { Geometry, Position } from 'geojson';
import type { CountryIdentity } from '@geogame/data';

export type LngLatTuple = [number, number];

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

type Vec3 = readonly [number, number, number];

/** `[lat, lng]` from the dataset → `[lng, lat]` for MapLibre. */
export function lngLatOf(country: CountryIdentity): LngLatTuple {
  const [lat, lng] = country.latlng;
  return [lng, lat];
}

export function toVec3(p: readonly [number, number]): Vec3 {
  const lo = p[0] * DEG;
  const la = p[1] * DEG;
  const c = Math.cos(la);
  return [c * Math.cos(lo), c * Math.sin(lo), Math.sin(la)];
}

export function fromVec3(v: Vec3): LngLatTuple {
  const [x, y, z] = v;
  const hyp = Math.hypot(x, y);
  return [Math.atan2(y, x) * RAD, Math.atan2(z, hyp) * RAD];
}

/** Great-circle separation in degrees. Antimeridian-safe by construction. */
export function angularDistanceDeg(
  a: readonly [number, number],
  b: readonly [number, number],
): number {
  const av = toVec3(a);
  const bv = toVec3(b);
  // Chord length rather than acos(dot): acos loses all its precision near zero
  // separation, which is exactly where the snapping code lives.
  const chord = Math.hypot(av[0] - bv[0], av[1] - bv[1], av[2] - bv[2]);
  return 2 * Math.asin(Math.min(1, chord / 2)) * RAD;
}

/**
 * Vector mean on the unit sphere. Averaging longitudes numerically puts the
 * centre of Oceania in the Atlantic; this does not.
 */
export function sphericalMean(points: readonly (readonly [number, number])[]): LngLatTuple {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const p of points) {
    const v = toVec3(p);
    x += v[0];
    y += v[1];
    z += v[2];
  }
  const len = Math.hypot(x, y, z);
  if (len < 1e-9) return [0, 0];
  return fromVec3([x / len, y / len, z / len]);
}

/**
 * Angular radius that covers `percentile` of the points, measured from
 * `centre`. The percentile (rather than the max) keeps one far-flung overseas
 * territory from zooming the whole camera out to nothing.
 */
export function angularSpreadDeg(
  centre: readonly [number, number],
  points: readonly (readonly [number, number])[],
  percentile = 0.9,
): number {
  if (points.length === 0) return 0;
  const d = points.map((p) => angularDistanceDeg(centre, p)).sort((a, b) => a - b);
  const idx = Math.min(d.length - 1, Math.max(0, Math.round((d.length - 1) * percentile)));
  return d[idx] ?? 0;
}

/**
 * Zoom level that frames a cap of the given angular radius.
 *
 * The globe fills the viewport at roughly z=0.9 showing a ~75° cap, and each
 * zoom step halves the visible angle. Approximate, deliberately: this only has
 * to look right, and `flyTo` interpolates anyway.
 */
export function zoomForAngularRadius(radiusDeg: number, minZoom = 0.4, maxZoom = 6): number {
  const r = Math.max(0.35, radiusDeg);
  const z = Math.log2(75 / r) + 0.9;
  return Math.min(maxZoom, Math.max(minZoom, Number(z.toFixed(3))));
}

/** Walk every coordinate of a GeoJSON geometry. */
export function eachPosition(geometry: Geometry, visit: (p: Position) => void): void {
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    const head = coords[0];
    if (typeof head === 'number') {
      visit(coords as Position);
      return;
    }
    for (const child of coords) walk(child);
  };
  if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries) eachPosition(g, visit);
    return;
  }
  walk(geometry.coordinates);
}

/**
 * Sampled vertices of a geometry, capped at `limit` points. Russia at 110m is
 * a few thousand vertices and we only need a shape estimate.
 */
export function sampleVertices(geometry: Geometry, limit = 400): LngLatTuple[] {
  const all: LngLatTuple[] = [];
  eachPosition(geometry, (p) => {
    const lng = p[0];
    const lat = p[1];
    if (typeof lng === 'number' && typeof lat === 'number') all.push([lng, lat]);
  });
  if (all.length <= limit) return all;
  const stride = Math.ceil(all.length / limit);
  const out: LngLatTuple[] = [];
  for (let i = 0; i < all.length; i += stride) {
    const v = all[i];
    if (v) out.push(v);
  }
  return out;
}

function pointInRing(lng: number, lat: number, ring: readonly Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (!a || !b) continue;
    const xi = a[0] ?? 0;
    const yi = a[1] ?? 0;
    const xj = b[0] ?? 0;
    const yj = b[1] ?? 0;
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInPolygon(lng: number, lat: number, rings: readonly Position[][]): boolean {
  const outer = rings[0];
  if (!outer || !pointInRing(lng, lat, outer)) return false;
  for (let k = 1; k < rings.length; k++) {
    const hole = rings[k];
    if (hole && pointInRing(lng, lat, hole)) return false;
  }
  return true;
}

/**
 * Ray-cast containment test in lng/lat space.
 *
 * Used only to sanity-check a country's curated centre against its own
 * polygon: nine of them (Kuwait and Israel most consequentially) carry a
 * `latlng` that lands inside a *neighbour*, which would fly the camera to the
 * wrong country. A ring that crosses the antimeridian can answer wrongly, but
 * the callers treat a false as "try the vertex mean instead", never as truth
 * about the world.
 */
export function pointInGeometry(point: readonly [number, number], geometry: Geometry): boolean {
  const [lng, lat] = point;
  if (geometry.type === 'Polygon') return pointInPolygon(lng, lat, geometry.coordinates);
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.some((rings) => pointInPolygon(lng, lat, rings));
  }
  if (geometry.type === 'GeometryCollection') {
    return geometry.geometries.some((g) => pointInGeometry(point, g));
  }
  return false;
}

function ringsOf(geometry: Geometry): Position[][] {
  if (geometry.type === 'Polygon') return geometry.coordinates;
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat();
  if (geometry.type === 'GeometryCollection') return geometry.geometries.flatMap(ringsOf);
  return [];
}

function ringArea(ring: readonly Position[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (!a || !b) continue;
    sum += ((b[0] ?? 0) - (a[0] ?? 0)) * ((b[1] ?? 0) + (a[1] ?? 0));
  }
  return Math.abs(sum / 2);
}

/**
 * A point guaranteed to be *inside* the geometry, biased toward its middle.
 *
 * `preferred` (the dataset's curated `latlng`) wins whenever it is already
 * inside. Otherwise this grid-searches the largest ring's bounding box and
 * keeps the interior sample furthest from any boundary vertex — a cheap
 * stand-in for the pole of inaccessibility, which is what you want for a long
 * thin country like Israel or Chile where the vertex mean falls outside.
 *
 * Returns null when nothing interior is found, so callers keep their own
 * fallback rather than being handed a point in the sea.
 */
export function representativePoint(
  geometry: Geometry,
  preferred: readonly [number, number],
  gridSteps = 40,
): LngLatTuple | null {
  if (pointInGeometry(preferred, geometry)) return [preferred[0], preferred[1]];

  const rings = ringsOf(geometry);
  let largest: readonly Position[] | null = null;
  let largestArea = -1;
  for (const ring of rings) {
    const a = ringArea(ring);
    if (a > largestArea) {
      largestArea = a;
      largest = ring;
    }
  }
  if (!largest || largest.length < 3) return null;

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const p of largest) {
    const lng = p[0] ?? 0;
    const lat = p[1] ?? 0;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  // A ring that wraps the antimeridian has a meaningless bounding box.
  if (maxLng - minLng >= 180) return null;

  let best: LngLatTuple | null = null;
  let bestClearance = -1;
  for (let i = 1; i < gridSteps; i++) {
    const lng = minLng + ((maxLng - minLng) * i) / gridSteps;
    for (let j = 1; j < gridSteps; j++) {
      const lat = minLat + ((maxLat - minLat) * j) / gridSteps;
      if (!pointInGeometry([lng, lat], geometry)) continue;
      let clearance = Infinity;
      for (const p of largest) {
        const d = Math.hypot((p[0] ?? 0) - lng, (p[1] ?? 0) - lat);
        if (d < clearance) clearance = d;
      }
      if (clearance > bestClearance) {
        bestClearance = clearance;
        best = [lng, lat];
      }
    }
  }
  return best;
}

/**
 * True when `target` sits on the hemisphere facing the camera at `centre`.
 *
 * MapLibre's globe hides back-facing geometry but `map.project()` will happily
 * hand back screen coordinates for a point behind the planet. Snapping uses
 * projected positions for the microstate dots, so it has to reject those
 * itself or a tap on Spain could snap to Nauru.
 */
export function isFrontFacing(
  centre: readonly [number, number],
  target: readonly [number, number],
  limitDeg = 88,
): boolean {
  return angularDistanceDeg(centre, target) <= limitDeg;
}

/**
 * Graticule as a set of densified line strings. Densification matters: a
 * meridian drawn as two endpoints renders as a chord through the planet under
 * globe projection.
 */
/**
 * The parallels worth drawing on a geography game's globe.
 *
 * Previously this was `for (lat = -60; lat <= 60; lat += 30)` — parallels at
 * -60/-30/0/30/60. Those are arbitrary grid steps, and the 30S line in
 * particular reads as "some line south of the Tropic of Capricorn", which is
 * exactly the question a player asked on seeing it.
 *
 * These five are the ones that mean something, and a player who wonders what
 * they are can look them up and learn something true. No labels (invariant 7) —
 * the lines carry it.
 */
export const MEANINGFUL_PARALLELS = [
  -66.5633, // Antarctic Circle
  -23.4365, // Tropic of Capricorn
  0, // Equator
  23.4365, // Tropic of Cancer
  66.5633, // Arctic Circle
] as const;

export function buildGraticule(stepDeg = 30, densifyDeg = 5): LngLatTuple[][] {
  const lines: LngLatTuple[][] = [];

  for (let lng = -180; lng < 180; lng += stepDeg) {
    const line: LngLatTuple[] = [];
    for (let lat = -85; lat <= 85; lat += densifyDeg) line.push([lng, lat]);
    lines.push(line);
  }

  for (const lat of MEANINGFUL_PARALLELS) {
    const line: LngLatTuple[] = [];
    for (let lng = -180; lng <= 180; lng += densifyDeg) line.push([lng, lat]);
    lines.push(line);
  }

  return lines;
}
